import {IconInfo} from "./IconInfo";
import {ColorPalette} from "./Color";
import {getAppState} from "../store/AppState";


export interface PointShapeInfo {
    id: string;
    iconInfo: IconInfo;
    name: string;
}


export class IconUtil {

    public static MIN_ICON_SIZE = 8;
    public static MAX_ICON_SIZE = 64;

    public static cachedDataURLsByIconInfo: Map<IconInfo, string> = new Map<IconInfo, string>();

    public static AVAILABLE_POINT_SHAPES: PointShapeInfo[] = [
        {id: "circle", name: "Circle", iconInfo: IconInfo.CIRCLE},
        {id: "location", name: "Location", iconInfo: IconInfo.LOCATION},
        {id: "aircraft", name: "Aircraft", iconInfo: IconInfo.AIRCRAFT},
        {id: "car", name: "Car", iconInfo: IconInfo.CAR},
        {id: "favorite", name: "Favorite", iconInfo: IconInfo.FAVORITE},
        {id: "heart", name: "Heart", iconInfo: IconInfo.HEART},
        {id: "house", name: "House", iconInfo: IconInfo.HOUSE},
        {id: "pin", name: "Pin", iconInfo: IconInfo.PIN},
        {id: "flag", name: "Flag", iconInfo: IconInfo.FLAG},
        {id: "vessel", name: "Vessel", iconInfo: IconInfo.VESSEL},
        {id: "arrow1", name: "Arrow 1", iconInfo: IconInfo.ARROW1},
        {id: "arrow2", name: "Arrow 2", iconInfo: IconInfo.ARROW2},
        {id: "arrow3", name: "Arrow 3", iconInfo: IconInfo.ARROW3},
    ];

    public static getSafeShape = (shape: string): string => {
        shape = shape?.toLowerCase();
        return !!IconUtil.AVAILABLE_POINT_SHAPES.find(info => info.id === shape) ? shape : IconUtil.AVAILABLE_POINT_SHAPES[0].id;
    }

    public static getPointShapeById = (id: string) => {
        return IconUtil.AVAILABLE_POINT_SHAPES.find(v => v.id === id);
    }

    public static iconInfoToURL = (iconInfo: IconInfo): string => {
        let dataUrl = IconUtil.cachedDataURLsByIconInfo.get(iconInfo);
        if (!dataUrl) {
            dataUrl = IconUtil.svgToDataURL(iconInfo.getSvgString(IconUtil.MAX_ICON_SIZE));
            IconUtil.cachedDataURLsByIconInfo.set(iconInfo, dataUrl);
        }
        return dataUrl;
    }

    public static svgToDataURL = (svg) => {
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    }

    public static getIconSizeForValue = (
        value: number,
        minValue: number,
        maxValue: number,
        minIconSize: number = IconUtil.MIN_ICON_SIZE,
        maxIconSize: number = IconUtil.MAX_ICON_SIZE
    ) => {
        return  minValue < maxValue ?
            minIconSize + (value - minValue) * (maxIconSize - minIconSize) / (maxValue - minValue) :
            minIconSize;
    }

    // code for creating an SVG donut chart from feature properties
    public static createClusterDonutIcon = (counts: number[], colorPalette: ColorPalette) => {
        const offsets = [];
        let total = 0;
        for (const count of counts) {
            offsets.push(total);
            total += count;
        }
        const fontSize =
            total >= 1000 ? 22 : total >= 100 ? 20 : total >= 10 ? 18 : 16;
        const r =
            total >= 1000 ? 50 : total >= 100 ? 32 : total >= 10 ? 24 : 18;
        const r0 = Math.round(r * 0.6);
        const w = r * 2;

        let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${IconUtil.MAX_ICON_SIZE}" height="${IconUtil.MAX_ICON_SIZE}" viewbox="0 0 ${w} ${w}" text-anchor="middle" style="font: ${fontSize}px sans-serif; display: block">`;

        for (let i = 0; i < counts.length; i++) {
            svg += IconUtil.donutSegment( offsets[i] / total, (offsets[i] + counts[i]) / total, r, r0, colorPalette[i].toHex() );
        }

        let isDark = getAppState().isDarkTheme();

        svg += `<circle cx="${r}" cy="${r}" r="${r0}" fill="${isDark ? '#111111' : 'white'}" />
            <text dominant-baseline="central" transform="translate(${r}, ${r})" fill="${isDark ? 'white' : 'black'}">
                ${total.toLocaleString()}
            </text>
            </svg>`;

        return IconUtil.svgToDataURL(svg);
    }

    private static donutSegment = (start, end, r, r0, color) => {
        if (end - start === 1) end -= 0.00001;
        const a0 = 2 * Math.PI * (start - 0.25);
        const a1 = 2 * Math.PI * (end - 0.25);
        const x0 = Math.cos(a0), y0 = Math.sin(a0);
        const x1 = Math.cos(a1), y1 = Math.sin(a1);
        const largeArc = end - start > 0.5 ? 1 : 0;

        // draw an SVG path
        return `<path d="M ${r + r0 * x0} ${r + r0 * y0} L ${r + r * x0} ${
            r + r * y0
        } A ${r} ${r} 0 ${largeArc} 1 ${r + r * x1} ${r + r * y1} L ${
            r + r0 * x1
        } ${r + r0 * y1} A ${r0} ${r0} 0 ${largeArc} 0 ${r + r0 * x0} ${
            r + r0 * y0
        }" fill="${color}" />`;
    }

}